import pripolsaLogo from "@/assets/clients/pripolsa.webp";
import hachasketchImg from "@/assets/clients/hachasketch.webp";
import gonerstudioLogo from "@/assets/clients/gonerstudio.webp";
import r8aLogo from "@/assets/clients/r8a.webp";
import timeworkLogo from "@/assets/clients/timework.webp";
import snowgranadaLogo from "@/assets/clients/snowgranada.webp";

export interface CaseStat {
  label: string;
  value: string;
}

export interface CaseServiceRef {
  label: string;
  slug: string; // matches /servicios/[slug]
}

export interface CaseStudy {
  slug: string;
  name: string;
  industry: string;
  logo: string;
  logoBg: string;
  logoRounded?: boolean;
  url: string;
  period: string;
  headline: string;
  stats: CaseStat[];
  shortDescription: string;
  // Long-form fields for detail page
  title: string; // SEO title
  metaDescription: string;
  startingPoint: string;
  whatWeDid: string;
  whatWeDidBullets?: string[];
  services: CaseServiceRef[];
  results: string;
  resultsHighlight?: string; // one-line conclusion
  featured?: boolean;
}

export const CASES: CaseStudy[] = [
  {
    slug: "pripolsa",
    name: "PRIPOLSA",
    industry: "Obras y Servicios",
    logo: pripolsaLogo,
    logoBg: "bg-white",
    url: "https://pripolsa.es/",
    period: "3 meses",
    headline: "+50% presupuestos emitidos",
    stats: [
      { label: "Presupuestado", value: "+3M€" },
      { label: "Cerrado", value: "500K€" },
      { label: "Presupuestos", value: "+50%" },
    ],
    shortDescription:
      "Automatizamos su proceso de presupuestación para estandarizar precios y generar propuestas en segundos.",
    title: "PRIPOLSA: +50% presupuestos y 3M€ presupuestados | Caso de Éxito Vibra",
    metaDescription:
      "Cómo Vibra Marketing ayudó a PRIPOLSA a presupuestar +3M€ y cerrar 500K€ en 3 meses automatizando su proceso de ventas.",
    startingPoint:
      "PRIPOLSA, empresa de obras y servicios, dependía de un proceso de presupuestación manual que consumía horas del equipo comercial y generaba precios inconsistentes entre proyectos.",
    whatWeDid:
      "Analizamos sus márgenes y su proceso de ventas al detalle, identificando cuellos de botella y oportunidades. Automatizamos el proceso para estandarizar precios y generar presupuestos automáticos en segundos, eliminando horas de trabajo manual.",
    whatWeDidBullets: [
      "Auditoría de márgenes y proceso comercial",
      "Detección de cuellos de botella en el ciclo de venta",
      "Automatización completa del proceso de presupuestación",
      "Estandarización de precios entre proyectos",
      "Generación de presupuestos en segundos, sin trabajo manual",
    ],
    services: [
      { label: "CRM y Embudo de Ventas", slug: "crm" },
      { label: "Consultoría", slug: "consultoria" },
    ],
    results:
      "Márgenes más altos, un equipo comercial más ágil y un proceso de venta predecible que escala sin contratar más personal. En 3 meses: más de 3M€ presupuestados, 500K€ cerrados y un 50% más de presupuestos emitidos.",
    resultsHighlight:
      "Un proceso de venta predecible que escala sin contratar más personal.",
    featured: true,
  },
  {

    slug: "hachasketch",
    name: "Hachasketch",
    industry: "Arte y Tatuaje",
    logo: hachasketchImg,
    logoBg: "bg-black",
    logoRounded: true,
    url: "https://www.instagram.com/hachasketch/",
    period: "3 meses",
    headline: "De 4K a 12K€/mes",
    stats: [
      { label: "Antes", value: "4K€/mes" },
      { label: "Después", value: "12K€/mes" },
      { label: "Crecimiento", value: "x3" },
    ],
    shortDescription:
      "Sistema completo de captación y automatización: campañas Meta, CRM y embudo que cualifica y agenda.",
    title: "Hachasketch: de 4K a 12K€/mes en 3 meses | Caso de Éxito Vibra",
    metaDescription:
      "Triplicamos la facturación de Hachasketch en 3 meses con Meta Ads, CRM y embudo de ventas automatizado.",
    startingPoint:
      "Hachasketch facturaba 4.000€/mes con un flujo de clientes irregular que dependía del boca a boca.",
    whatWeDid:
      "Implementamos nuestro sistema completo de captación y automatización: campañas optimizadas para su público, CRM con seguimiento automático de cada lead, embudo que cualifica y agenda citas sin intervención manual, y gestión centralizada de la comunicación.",
    whatWeDidBullets: [
      "Campañas Meta Ads optimizadas para su público objetivo",
      "CRM con seguimiento automático de cada lead",
      "Embudo que cualifica y agenda citas sin intervención",
      "Gestión centralizada de toda la comunicación",
    ],
    services: [
      { label: "Publicidad Meta", slug: "publicidad-meta" },
      { label: "CRM y Embudo de Ventas", slug: "crm" },
    ],
    results:
      "En 3 meses, la facturación pasó de 4.000€ a 12.000€/mes — el triple — con el mismo equipo y las mismas horas de trabajo.",
    resultsHighlight: "El triple de facturación con el mismo equipo.",
  },
  {
    slug: "goner-studio",
    name: "Goner Studio",
    industry: "Agencia de Marketing",
    logo: gonerstudioLogo,
    logoBg: "bg-black",
    url: "https://gonerstudio.com",
    period: "12 meses",
    headline: "De 3K a +20K€/mes",
    stats: [
      { label: "Antes", value: "3K€/mes" },
      { label: "Después", value: "20K€/mes" },
      { label: "Crecimiento", value: "x6.6" },
    ],
    shortDescription:
      "Nueva propuesta comercial, embudo automatizado y pasarela de suscripciones recurrentes.",
    title: "Goner Studio: de 3K a +20K€/mes en 12 meses | Caso de Éxito Vibra",
    metaDescription:
      "Cómo Vibra Marketing llevó a Goner Studio de 3K€ a 20K€/mes con una nueva estructura comercial y embudo escalable.",
    startingPoint:
      "Goner Studio dependía de referidos y contactos puntuales, con una propuesta comercial difícil de escalar.",
    whatWeDid:
      "Reorganizamos por completo su propuesta comercial: nueva estructura de precios, embudo automatizado con tareas complejas, centralita de llamadas integrada y pasarela de suscripciones recurrentes.",
    whatWeDidBullets: [
      "Nueva estructura de precios escalable",
      "Embudo automatizado con tareas complejas",
      "Centralita de llamadas integrada",
      "Pasarela de suscripciones recurrentes",
    ],
    services: [
      { label: "Consultoría", slug: "consultoria" },
      { label: "CRM y Embudo de Ventas", slug: "crm" },
    ],
    results:
      "Un motor de ventas predecible que genera clientes cada semana. De 3.000€/mes a superar los 20.000€/mes de forma consistente.",
    resultsHighlight: "Un motor de ventas que genera clientes cada semana.",
    featured: true,
  },
  {
    slug: "r8a-reformas",
    name: "R8A Reformas",
    industry: "Reformas Alto Standing",
    logo: r8aLogo,
    logoBg: "bg-black",
    url: "https://roblesochoareforma.com/",
    period: "3 meses",
    headline: "+1M€ presupuestados",
    stats: [
      { label: "Presupuestado", value: "+1M€" },
      { label: "Cerrado", value: "200K€" },
    ],
    shortDescription:
      "Landing para proyectos premium, embudo automatizado y presupuestos automáticos que filtran cada lead.",
    title: "R8A Reformas: +1M€ presupuestados en 3 meses | Caso de Éxito Vibra",
    metaDescription:
      "R8A Reformas presupuestó +1M€ y cerró 200K€ en 3 meses con landing premium y embudo automatizado de Vibra.",
    startingPoint:
      "Empresa de reformas premium con tickets elevados que necesitaba un flujo constante de proyectos cualificados, no curiosos.",
    whatWeDid:
      "Landing especializada en captar proyectos premium, embudo de ventas automatizado y un sistema de presupuestos automáticos que filtra y cualifica cada solicitud antes de llegar al equipo comercial.",
    whatWeDidBullets: [
      "Landing especializada en proyectos premium",
      "Embudo de ventas automatizado",
      "Sistema de presupuestos automáticos que filtran cada lead",
      "Cualificación previa antes del equipo comercial",
    ],
    services: [
      { label: "Landing Page", slug: "landing-page" },
      { label: "Publicidad Meta", slug: "publicidad-meta" },
      { label: "CRM y Embudo de Ventas", slug: "crm" },
    ],
    results:
      "En 3 meses, más de 1M€ presupuestados y 200.000€ cerrados en proyectos de reforma integral.",
    resultsHighlight: "Solo entran al equipo los proyectos que sí encajan.",
  },
  {

    slug: "timework",
    name: "Timework",
    industry: "SaaS / Software",
    logo: timeworkLogo,
    logoBg: "bg-white",
    url: "https://timework.es/",
    period: "3 meses",
    headline: "+1000 licencias y 25K€/mes",
    stats: [
      { label: "Demos", value: "350+" },
      { label: "Licencias", value: "1000+" },
      { label: "MRR", value: "25K€" },
    ],
    shortDescription:
      "Sistema de captación, suscripciones, demos y atribución técnica (píxel + API de conversiones).",
    title: "Timework: +1.000 licencias y 25K€/mes MRR en 3 meses | Vibra",
    metaDescription:
      "Lanzamiento SaaS de Timework con +1000 licencias, 350 demos y 25K€/mes MRR en 3 meses con Vibra Marketing.",
    startingPoint:
      "Timework, software SaaS de control y gestión de equipos, necesitaba un sistema de captación y conversión a suscripción desde cero.",
    whatWeDid:
      "Diseñamos la landing, implementamos pasarela de pago para suscripciones mensuales, embudo automático de captación, sistema de citas integrado para demos y toda la implementación técnica de atribución: píxel y API de conversiones.",
    whatWeDidBullets: [
      "Landing diseñada para conversión SaaS",
      "Pasarela de pago para suscripciones mensuales",
      "Embudo automático de captación + agendado de demos",
      "Atribución técnica completa: píxel + API de conversiones",
    ],
    services: [
      { label: "Landing Page", slug: "landing-page" },
      { label: "Google Ads", slug: "google-ads" },
      { label: "Publicidad Meta", slug: "publicidad-meta" },
      { label: "CRM y Embudo de Ventas", slug: "crm" },
    ],
    results:
      "En 3 meses: más de 350 demos agendadas, más de 1.000 licencias y una facturación recurrente superior a 25.000€/mes.",
    resultsHighlight: "Un SaaS escalando a 25K€/mes de MRR en solo 90 días.",
    featured: true,
  },
  {
    slug: "snowgranada",
    name: "SnowGranada",
    industry: "Alquiler Vacacional",
    logo: snowgranadaLogo,
    logoBg: "bg-blue-600",
    url: "https://snowgranada.com/",
    period: "2 meses",
    headline: "+35% reservas",
    stats: [
      { label: "Reservas", value: "+35%" },
      { label: "Periodo", value: "2 meses" },
    ],
    shortDescription:
      "Campañas multilingües en Meta, TikTok y Google con embudos adaptados a cada mercado.",
    title: "SnowGranada: +35% de reservas en 2 meses | Caso de Éxito Vibra",
    metaDescription:
      "SnowGranada aumentó sus reservas un 35% en 2 meses con campañas multilingües y embudos automatizados.",
    startingPoint:
      "Empresa de alquiler vacacional en la Sierra de Granada con público internacional y estacionalidad marcada.",
    whatWeDid:
      "Campañas en Meta, TikTok y Google en varios idiomas, embudos automatizados multilingües adaptados a cada mercado y una pasarela de pago especializada para el sector vacacional.",
    whatWeDidBullets: [
      "Campañas Meta + TikTok + Google en varios idiomas",
      "Embudos automatizados multilingües por mercado",
      "Pasarela de pago especializada para alquiler vacacional",
    ],
    services: [
      { label: "Publicidad Meta", slug: "publicidad-meta" },
      { label: "Publicidad TikTok", slug: "publicidad-tiktok" },
      { label: "Google Ads", slug: "google-ads" },
    ],
    results:
      "En solo 2 meses, las reservas aumentaron un 35% respecto al periodo anterior.",
    resultsHighlight: "Una temporada llena con público internacional.",
  },
];

export const CASE_SLUGS = CASES.map((c) => c.slug);

export const getCaseBySlug = (slug: string): CaseStudy | undefined =>
  CASES.find((c) => c.slug === slug);
